import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '../stores/auth';
import { getCurrentSubdomain, hasSubdomain, extractSubdomain } from '../utils/subdomain';
import { hasPermission, CLASS_PERMISSIONS } from '../utils/permissions';

// Lazy load Layout component
const Layout = () => import('../components/Layout.vue');

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/subdomain',
      name: 'subdomain-input',
      component: () =>
        import(/* webpackChunkName: "subdomain-input" */ '../views/SubdomainInput.vue'),
      meta: { requiresAuth: false, requiresSubdomain: false },
    },
    {
      path: '/login',
      name: 'login',
      component: () => import(/* webpackChunkName: "login" */ '../views/Login.vue'),
      meta: { requiresAuth: false, requiresSubdomain: true },
    },
    {
      path: '/setup-password',
      name: 'setup-password',
      component: () =>
        import(/* webpackChunkName: "setup-password" */ '../views/SetupPassword.vue'),
      meta: { requiresAuth: false, requiresSubdomain: true },
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () =>
        import(/* webpackChunkName: "forgot-password" */ '../views/ForgotPassword.vue'),
      meta: { requiresAuth: false, requiresSubdomain: true },
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: () =>
        import(/* webpackChunkName: "reset-password" */ '../views/ResetPassword.vue'),
      meta: { requiresAuth: false, requiresSubdomain: true },
    },
    {
      path: '/apply',
      name: 'public-apply',
      component: () =>
        import(/* webpackChunkName: "public-apply" */ '../views/public/PublicAdmissionApply.vue'),
      meta: { requiresAuth: false, requiresSubdomain: true },
    },
    {
      path: '/',
      component: Layout,
      meta: { requiresAuth: true, requiresSubdomain: true },
      children: [
        {
          path: '',
          redirect: '/dashboard',
        },
        {
          path: 'dashboard',
          name: 'dashboard',
          component: () => import(/* webpackChunkName: "dashboard" */ '../views/Dashboard.vue'),
        },
        {
          path: 'students',
          name: 'students',
          component: () =>
            import(/* webpackChunkName: "students" */ '../views/students/StudentsList.vue'),
        },
        {
          path: 'students/applications',
          name: 'admission-applications',
          component: () =>
            import(
              /* webpackChunkName: "admission-applications" */ '../views/students/AdmissionApplicationsList.vue'
            ),
        },
        {
          path: 'students/applications/:id',
          name: 'admission-application-detail',
          component: () =>
            import(
              /* webpackChunkName: "admission-application-detail" */ '../views/students/AdmissionApplicationDetail.vue'
            ),
        },
        {
          path: 'students/create',
          name: 'enroll-student',
          component: () =>
            import(/* webpackChunkName: "enroll-student" */ '../views/students/EnrollStudent.vue'),
        },
        {
          path: 'students/:id',
          name: 'view-student',
          component: () =>
            import(/* webpackChunkName: "view-student" */ '../views/students/ViewStudent.vue'),
        },
        {
          path: 'staff',
          name: 'staff',
          component: () => import(/* webpackChunkName: "staff" */ '../views/staff/StaffList.vue'),
        },
        {
          path: 'staff/create',
          name: 'create-staff',
          component: () =>
            import(/* webpackChunkName: "create-staff" */ '../views/staff/CreateStaff.vue'),
        },
        {
          path: 'staff/:id',
          name: 'view-staff',
          component: () =>
            import(/* webpackChunkName: "view-staff" */ '../views/staff/ViewStaff.vue'),
        },
        {
          path: 'staff/:id/edit',
          name: 'edit-staff',
          component: () =>
            import(/* webpackChunkName: "edit-staff" */ '../views/staff/EditStaff.vue'),
        },
        {
          path: 'classes',
          name: 'classes',
          component: () =>
            import(/* webpackChunkName: "classes" */ '../views/classes/ClassesList.vue'),
          meta: { requiresPermission: CLASS_PERMISSIONS.VIEW },
        },
        {
          path: 'classes/create',
          name: 'create-class',
          component: () =>
            import(/* webpackChunkName: "create-class" */ '../views/classes/CreateClass.vue'),
          meta: { requiresPermission: CLASS_PERMISSIONS.CREATE },
        },
        {
          path: 'classes/:id',
          name: 'view-class',
          component: () =>
            import(/* webpackChunkName: "view-class" */ '../views/classes/ViewClass.vue'),
          meta: { requiresPermission: CLASS_PERMISSIONS.VIEW },
        },
        {
          path: 'classes/:id/edit',
          name: 'edit-class',
          component: () =>
            import(/* webpackChunkName: "edit-class" */ '../views/classes/EditClass.vue'),
          meta: { requiresPermission: CLASS_PERMISSIONS.UPDATE },
        },
        {
          path: 'subjects',
          name: 'subjects',
          component: () =>
            import(/* webpackChunkName: "subjects" */ '../views/subjects/SubjectsList.vue'),
        },
        {
          path: 'attendance',
          name: 'attendance',
          component: () =>
            import(/* webpackChunkName: "attendance" */ '../views/attendance/Attendance.vue'),
        },
        {
          path: 'exams',
          name: 'exams',
          component: () => import(/* webpackChunkName: "exams" */ '../views/exams/ExamsList.vue'),
        },
        {
          path: 'fees',
          name: 'fees',
          component: () => import(/* webpackChunkName: "fees" */ '../views/fees/FeesList.vue'),
        },
        {
          path: 'timetable',
          name: 'timetable',
          component: () =>
            import(/* webpackChunkName: "timetable" */ '../views/timetable/Timetable.vue'),
        },
        {
          path: 'library',
          name: 'library',
          component: () => import(/* webpackChunkName: "library" */ '../views/library/Library.vue'),
        },
        {
          path: 'transport',
          name: 'transport',
          component: () =>
            import(/* webpackChunkName: "transport" */ '../views/transport/Transport.vue'),
        },
        {
          path: 'inventory',
          name: 'inventory',
          component: () =>
            import(/* webpackChunkName: "inventory" */ '../views/inventory/Inventory.vue'),
        },
        {
          path: 'communications',
          name: 'communications',
          component: () =>
            import(
              /* webpackChunkName: "communications" */ '../views/communications/Communications.vue'
            ),
        },
        {
          path: 'reports',
          name: 'reports',
          component: () => import(/* webpackChunkName: "reports" */ '../views/reports/Reports.vue'),
        },
        {
          path: 'settings',
          name: 'settings',
          component: () =>
            import(/* webpackChunkName: "settings" */ '../views/settings/Settings.vue'),
        },
        {
          path: 'settings/customization',
          name: 'customization',
          component: () =>
            import(/* webpackChunkName: "customization" */ '../views/settings/Customization.vue'),
        },
        {
          path: 'settings/general-information',
          name: 'general-information',
          component: () =>
            import(
              /* webpackChunkName: "general-information" */ '../views/settings/GeneralInformation.vue'
            ),
        },
        {
          path: 'settings/notifications',
          name: 'notifications',
          component: () =>
            import(/* webpackChunkName: "notifications" */ '../views/settings/Notifications.vue'),
        },
        {
          path: 'settings/email',
          name: 'email-settings',
          component: () =>
            import(/* webpackChunkName: "email-settings" */ '../views/settings/EmailSettings.vue'),
        },
        {
          path: 'settings/sms',
          name: 'sms-settings',
          component: () =>
            import(/* webpackChunkName: "sms-settings" */ '../views/settings/SmsSettings.vue'),
        },
        {
          path: 'settings/notification-templates',
          name: 'notification-templates',
          component: () =>
            import(
              /* webpackChunkName: "notification-templates" */ '../views/settings/NotificationTemplates.vue'
            ),
        },
        {
          path: 'settings/security',
          name: 'security-settings',
          component: () =>
            import(
              /* webpackChunkName: "security-settings" */ '../views/settings/SecuritySettings.vue'
            ),
        },
        {
          path: 'settings/payments-subscriptions',
          name: 'payments-subscriptions',
          component: () =>
            import(
              /* webpackChunkName: "payments-subscriptions" */ '../views/settings/PaymentsAndSubscriptions.vue'
            ),
        },
        {
          path: 'settings/bulk-upload',
          name: 'bulk-upload',
          component: () =>
            import(/* webpackChunkName: "bulk-upload" */ '../views/settings/BulkUpload.vue'),
        },
        {
          path: 'settings/curriculum',
          name: 'curriculum-settings',
          component: () =>
            import(
              /* webpackChunkName: "curriculum-settings" */ '../views/settings/Curriculum.vue'
            ),
        },
        {
          path: 'settings/curriculum/view/:id?',
          name: 'curriculum-detail',
          component: () =>
            import(
              /* webpackChunkName: "curriculum-detail" */ '../views/settings/CurriculumDetail.vue'
            ),
        },
        {
          path: 'settings/curriculum/override/create',
          name: 'create-override',
          component: () =>
            import(
              /* webpackChunkName: "create-override" */ '../views/settings/CreateOverride.vue'
            ),
        },
        {
          path: 'settings/curriculum/override/:id',
          name: 'edit-override',
          component: () =>
            import(/* webpackChunkName: "edit-override" */ '../views/settings/CreateOverride.vue'),
        },
        {
          path: 'settings/academic-years',
          name: 'academic-years',
          component: () =>
            import(
              /* webpackChunkName: "academic-years" */ '../views/settings/academic-years/AcademicYearsList.vue'
            ),
        },
        {
          path: 'settings/academic-years/create',
          name: 'create-academic-year',
          component: () =>
            import(
              /* webpackChunkName: "create-academic-year" */ '../views/settings/academic-years/CreateAcademicYear.vue'
            ),
        },
        {
          path: 'settings/academic-years/sync',
          name: 'sync-academic-year',
          component: () =>
            import(
              /* webpackChunkName: "sync-academic-year" */ '../views/settings/academic-years/SyncAcademicYear.vue'
            ),
        },
        {
          path: 'settings/academic-years/:id',
          name: 'view-academic-year',
          component: () =>
            import(
              /* webpackChunkName: "view-academic-year" */ '../views/settings/academic-years/ViewAcademicYear.vue'
            ),
        },
        {
          path: 'settings/academic-years/:id/edit',
          name: 'edit-academic-year',
          component: () =>
            import(
              /* webpackChunkName: "edit-academic-year" */ '../views/settings/academic-years/EditAcademicYear.vue'
            ),
        },
        {
          path: 'settings/invoices/:id',
          name: 'invoice-detail',
          component: () =>
            import(/* webpackChunkName: "invoice-detail" */ '../views/settings/InvoiceDetail.vue'),
        },
        {
          path: 'settings/security/users/create',
          name: 'create-user',
          component: () =>
            import(
              /* webpackChunkName: "create-user" */ '../views/settings/security/CreateUser.vue'
            ),
        },
        {
          path: 'settings/security/users/:id/edit',
          name: 'edit-user',
          component: () =>
            import(/* webpackChunkName: "edit-user" */ '../views/settings/security/EditUser.vue'),
        },
        {
          path: 'settings/security/roles/create',
          name: 'create-role',
          component: () =>
            import(
              /* webpackChunkName: "create-role" */ '../views/settings/security/CreateRole.vue'
            ),
        },
        {
          path: 'settings/security/groups/create',
          name: 'create-group',
          component: () =>
            import(
              /* webpackChunkName: "create-group" */ '../views/settings/security/CreateGroup.vue'
            ),
        },
        {
          path: 'settings/policies',
          name: 'policies',
          component: () =>
            import(/* webpackChunkName: "policies" */ '../views/settings/Policies.vue'),
        },
      ],
    },
  ],
});

// Navigation guard
router.beforeEach((to, from, next) => {
  const authStore = useAuthStore();
  const currentSubdomain = getCurrentSubdomain();
  const hasSubdomainInUrl = extractSubdomain(window.location.hostname) !== null;
  const hasSubdomainAnywhere = hasSubdomain(); // Includes localStorage check

  // Handle root path - redirect based on subdomain presence
  if (to.path === '/' && !hasSubdomainInUrl) {
    next({ name: 'subdomain-input' });
    return;
  }

  // Check if route requires subdomain
  if (to.meta.requiresSubdomain && !hasSubdomainAnywhere) {
    // No subdomain in URL or localStorage, redirect to subdomain input
    next({ name: 'subdomain-input' });
    return;
  }

  // If route doesn't require subdomain but we have one, allow it (e.g., subdomain input page)
  if (!to.meta.requiresSubdomain && hasSubdomainInUrl && to.name !== 'subdomain-input') {
    // We have a subdomain but trying to access a non-subdomain route, redirect to dashboard
    next({ name: 'dashboard' });
    return;
  }

  // Validate token subdomain matches current subdomain (if authenticated)
  if (authStore.isAuthenticated && currentSubdomain && authStore.user?.tenantId) {
    const tokenSubdomain = authStore.user.subdomain;
    if (tokenSubdomain && tokenSubdomain !== currentSubdomain) {
      // Token subdomain doesn't match current subdomain
      authStore.clearAuthState();
      next({ name: 'subdomain-input' });
      return;
    }
  }

  // If route requires auth and user is not authenticated
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    // Check if we have a subdomain (from URL or localStorage)
    const hasSubdomain = getCurrentSubdomain() !== null;
    if (hasSubdomain) {
      next({ name: 'login', query: { redirect: to.fullPath } });
    } else {
      next({ name: 'subdomain-input' });
    }
    return;
  }

  // If user is authenticated and trying to access login page
  if (to.name === 'login' && authStore.isAuthenticated) {
    next({ name: 'dashboard' });
    return;
  }

  // If user is on subdomain input page but already has subdomain, redirect to login
  if (to.name === 'subdomain-input' && hasSubdomainInUrl) {
    next({ name: 'login' });
    return;
  }

  // Check route permissions
  if (to.meta.requiresPermission && authStore.isAuthenticated) {
    const requiredPermission = to.meta.requiresPermission as string;
    if (!hasPermission(requiredPermission)) {
      // User doesn't have required permission
      next({ name: 'dashboard' });
      return;
    }
  }

  next();
});

export default router;
